import React, { useState } from 'react';

const API_URL = 'https://hello-reviews-api.b-9f2.workers.dev';

interface OutreachLog {
  id: number;
  business_name: string;
  contact_name?: string;
  contact_method?: string;
  message?: string;
  status?: string;
  follow_up_date?: string;
  notes?: string;
  hidden?: boolean;
  hidden_at?: string;
  created_at: string;
}

interface QuickLogProps {
  subPage: string | null;
  pageId: string | null;
}

export default function QuickLog({ subPage, pageId }: QuickLogProps) {
  const [recentLogs, setRecentLogs] = useState<OutreachLog[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [editingLog, setEditingLog] = useState<OutreachLog | null>(null);
  const [showHidden, setShowHidden] = useState(false);
  const [hiddenCount, setHiddenCount] = useState(0);

  // Simple form state
  const [businessName, setBusinessName] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactMethod, setContactMethod] = useState('sms');
  const [message, setMessage] = useState('');
  const [followUpDays, setFollowUpDays] = useState('3');
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState('sent');

  // Determine which view to show based on subPage
  const currentView = subPage === 'recent' ? 'recent' : subPage === 'edit' ? 'edit' : 'form';

  // Load recent logs
  React.useEffect(() => {
    const hiddenParam = showHidden ? 'true' : 'false';
    fetch(`${API_URL}/api/research/outreach?hidden=${hiddenParam}`)
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          setRecentLogs(data.data || []);
        }
      })
      .catch(err => console.error('Failed to fetch logs:', err));
  }, [showHidden]);

  // Load hidden count
  React.useEffect(() => {
    fetch(`${API_URL}/api/research/outreach?hidden=true`)
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          setHiddenCount(data.data?.length || 0);
        }
      })
      .catch(err => console.error('Failed to fetch hidden count:', err));
  }, [recentLogs]);

  // Load specific log for editing
  React.useEffect(() => {
    if (subPage === 'edit' && pageId && recentLogs.length > 0) {
      const log = recentLogs.find(l => l.id === parseInt(pageId));
      if (log) {
        setEditingLog(log);
        setBusinessName(log.business_name);
        setContactName(log.contact_name || '');
        setContactMethod(log.contact_method || 'sms');
        setMessage(log.message || '');
        setStatus(log.status || 'sent');
        setNotes(log.notes || '');
        // Calculate days from follow_up_date
        if (log.follow_up_date) {
          const followUpDate = new Date(log.follow_up_date);
          const createdDate = new Date(log.created_at);
          const daysDiff = Math.ceil((followUpDate.getTime() - createdDate.getTime()) / (1000 * 60 * 60 * 24));
          setFollowUpDays(daysDiff.toString());
        }
      }
    }
  }, [subPage, pageId, recentLogs]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!businessName.trim()) return;

    setSubmitting(true);
    try {
      const isEditing = currentView === 'edit' && editingLog;
      const url = isEditing
        ? `${API_URL}/api/research/outreach/${editingLog.id}`
        : `${API_URL}/api/research/outreach`;

      const method = isEditing ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          business_name: businessName,
          contact_name: contactName || null,
          contact_method: contactMethod,
          message: message || null,
          status: status,
          notes: notes || null,
          follow_up_days: followUpDays ? parseInt(followUpDays) : null
        })
      });

      const data = await response.json();
      if (data.success) {
        if (isEditing) {
          // Update in recent logs
          setRecentLogs(recentLogs.map(log =>
            log.id === editingLog.id ? { ...log, business_name: businessName, contact_name: contactName, contact_method: contactMethod, message, status, notes } : log
          ));
          alert('✅ Outreach updated!');
          window.location.hash = '#log/recent';
        } else {
          // Add to recent logs
          setRecentLogs([data.data, ...recentLogs]);
          // Reset form
          setBusinessName('');
          setContactName('');
          setMessage('');
          setFollowUpDays('3');
          setNotes('');
          setStatus('sent');
          alert('✅ Outreach logged!');
        }
      }
    } catch (err) {
      console.error('Failed to save outreach:', err);
      alert('Failed to save outreach');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleHidden = async (id: number, currentlyHidden: boolean) => {
    try {
      const endpoint = currentlyHidden ? 'unhide' : 'hide';
      const response = await fetch(`${API_URL}/api/research/outreach/${id}/${endpoint}`, {
        method: 'PATCH'
      });

      if (response.ok) {
        // Remove from current view (will reload from correct filter)
        setRecentLogs(recentLogs.filter(log => log.id !== id));
        // Update counts
        if (currentlyHidden) {
          setHiddenCount(hiddenCount - 1);
        } else {
          setHiddenCount(hiddenCount + 1);
        }
      }
    } catch (err) {
      console.error('Failed to toggle hidden:', err);
    }
  };

  const handleDelete = async (id: number, businessName: string) => {
    if (!confirm(`Delete outreach to ${businessName}? This cannot be undone.`)) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/research/outreach/${id}`, {
        method: 'DELETE'
      });

      const data = await response.json();
      if (data.success) {
        setRecentLogs(recentLogs.filter(log => log.id !== id));
        alert('✅ Outreach deleted');
      }
    } catch (err) {
      console.error('Failed to delete outreach:', err);
      alert('Failed to delete outreach');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-4 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {currentView === 'edit' ? 'Edit Outreach' : 'Quick Log'}
          </h1>
          <p className="text-gray-600">
            {currentView === 'edit' ? 'Update outreach details' : 'Log outreach in seconds'}
          </p>
          {currentView !== 'edit' && (
            <div className="flex gap-2 mt-4">
              <a
                href="#log"
                className={`px-4 py-2 rounded-lg font-medium ${
                  currentView === 'form'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 border border-gray-300'
                }`}
              >
                📝 Log New
              </a>
              <a
                href="#log/recent"
                className={`px-4 py-2 rounded-lg font-medium ${
                  currentView === 'recent'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 border border-gray-300'
                }`}
              >
                📋 Recent ({recentLogs.length})
              </a>
            </div>
          )}
        </div>

        {/* Quick Log Form */}
        {(currentView === 'form' || currentView === 'edit') && (
          <div className="bg-white p-6 rounded-lg shadow-md mb-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Business Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Business Name *
                </label>
                <input
                  type="text"
                  required
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                  className="w-full px-4 py-3 text-lg border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., MY SKINSATIONAL"
                  autoFocus
                />
              </div>

              {/* Contact Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Contact Name
                </label>
                <input
                  type="text"
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  className="w-full px-4 py-3 text-lg border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Gia Parker"
                />
              </div>

              {/* Contact Method */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  How?
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {['sms', 'email', 'call', 'linkedin', 'x', 'instagram', 'in_person'].map((method) => (
                    <button
                      key={method}
                      type="button"
                      onClick={() => setContactMethod(method)}
                      className={`px-4 py-3 rounded-lg font-medium capitalize ${
                        contactMethod === method
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {method === 'in_person' ? 'In Person' : method === 'x' ? 'X' : method}
                    </button>
                  ))}
                </div>
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  What you said
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-3 text-lg border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Hey Gia, when you have 5 minutes..."
                />
              </div>

              {/* Follow Up */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Follow up in...
                </label>
                <div className="grid grid-cols-5 gap-2">
                  {['1', '3', '5', '7', '10'].map((days) => (
                    <button
                      key={days}
                      type="button"
                      onClick={() => setFollowUpDays(days)}
                      className={`px-4 py-3 rounded-lg font-medium ${
                        followUpDays === days
                          ? 'bg-green-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {days}d
                    </button>
                  ))}
                </div>
              </div>

              {/* Status (show on edit or create) */}
              {currentView === 'edit' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Status
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { value: 'sent', label: 'Sent' },
                      { value: 'replied', label: 'Replied' },
                      { value: 'scheduled', label: 'Scheduled' },
                      { value: 'no_response', label: 'No Response' }
                    ].map((statusOption) => (
                      <button
                        key={statusOption.value}
                        type="button"
                        onClick={() => setStatus(statusOption.value)}
                        className={`px-4 py-3 rounded-lg font-medium ${
                          status === statusOption.value
                            ? 'bg-purple-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {statusOption.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes {currentView === 'form' && '(optional)'}
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-3 text-lg border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Follow-up notes, conversation details..."
                />
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={submitting || !businessName.trim()}
                className="w-full bg-blue-600 text-white font-bold text-lg px-6 py-4 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? (currentView === 'edit' ? 'Saving...' : 'Logging...') : (currentView === 'edit' ? '💾 Save Changes' : '✅ Log Outreach')}
              </button>

              {/* Cancel button for edit view */}
              {currentView === 'edit' && (
                <a
                  href="#log/recent"
                  className="block w-full bg-gray-100 text-gray-700 font-bold text-lg px-6 py-4 rounded-lg hover:bg-gray-200 transition-colors text-center"
                >
                  Cancel
                </a>
              )}
            </form>
          </div>
        )}

        {/* Recent Logs */}
        {currentView === 'recent' && (
          <div className="space-y-4">
            {/* Header with toggle */}
            <div className="flex justify-between items-center bg-white p-4 rounded-lg shadow-md">
              <h2 className="text-xl font-bold text-gray-900">
                {showHidden ? '📋 Hidden Items' : '📋 Recent'}
                {!showHidden && recentLogs.length > 0 && (
                  <span className="text-sm font-normal text-gray-600 ml-2">
                    ({recentLogs.length} need review)
                  </span>
                )}
              </h2>
              <button
                onClick={() => setShowHidden(!showHidden)}
                className="px-4 py-2 bg-gray-100 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
              >
                {showHidden ? '← Show Active' : `Show Hidden (${hiddenCount})`}
              </button>
            </div>

            {/* Empty state */}
            {recentLogs.length === 0 && !showHidden && (
              <div className="text-center py-12 bg-white rounded-lg shadow-lg">
                <div className="text-6xl mb-4">🎉</div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">All caught up!</h3>
                <p className="text-gray-600 mb-6">No outreach needs review.</p>
                {hiddenCount > 0 && (
                  <button
                    onClick={() => setShowHidden(true)}
                    className="text-blue-600 hover:underline font-medium"
                  >
                    Show {hiddenCount} Hidden Items
                  </button>
                )}
              </div>
            )}

            {recentLogs.length === 0 && showHidden && (
              <div className="text-center py-12 bg-white rounded-lg shadow">
                <p className="text-gray-500">No hidden items</p>
              </div>
            )}

            {recentLogs.map((log) => (
              <div
                key={log.id}
                className={`bg-white p-4 rounded-lg shadow-md cursor-pointer transition-all ${
                  log.hidden ? 'opacity-60' : 'hover:shadow-lg'
                }`}
                onClick={() => handleToggleHidden(log.id, !!log.hidden)}
              >
                <div className="flex justify-between items-start mb-2">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900">{log.business_name}</h3>
                    {log.contact_name && (
                      <p className="text-sm text-gray-600">Contact: {log.contact_name}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium capitalize">
                      {log.contact_method || 'sms'}
                    </span>
                    {log.status && log.status !== 'sent' && (
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        log.status === 'replied' ? 'bg-green-100 text-green-800' :
                        log.status === 'scheduled' ? 'bg-purple-100 text-purple-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {log.status.replace('_', ' ')}
                      </span>
                    )}
                  </div>
                </div>
                {log.message && (
                  <p className="text-gray-700 text-sm mb-2 italic">"{log.message}"</p>
                )}
                {log.notes && (
                  <p className="text-gray-600 text-sm mb-2 bg-gray-50 p-2 rounded">
                    <strong>Notes:</strong> {log.notes}
                  </p>
                )}
                <div className="flex justify-between items-center text-sm text-gray-500 mb-3">
                  <span>{new Date(log.created_at).toLocaleDateString()}</span>
                  {log.follow_up_date && (
                    <span className="text-green-600 font-medium">
                      Follow up: {new Date(log.follow_up_date).toLocaleDateString()}
                    </span>
                  )}
                </div>
                {/* Action Buttons */}
                <div className="flex gap-2 pt-3 border-t border-gray-200" onClick={(e) => e.stopPropagation()}>
                  <a
                    href={`#log/edit/${log.id}`}
                    className="flex-1 bg-blue-50 text-blue-700 font-medium px-4 py-2 rounded-lg hover:bg-blue-100 transition-colors text-center"
                  >
                    ✏️ Edit
                  </a>
                  <button
                    onClick={() => handleDelete(log.id, log.business_name)}
                    className="flex-1 bg-red-50 text-red-700 font-medium px-4 py-2 rounded-lg hover:bg-red-100 transition-colors"
                  >
                    🗑️ Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Back Link */}
        <div className="mt-8 text-center">
          <a href="/#research" className="text-blue-600 hover:underline">
            ← Back to Research Dashboard
          </a>
        </div>
      </div>
    </div>
  );
}
